import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCRM } from '../../utils/CRMContext';
import { EnhancedDeal, Pipeline, PipelineStage } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useLeadTimeContext } from '../../context/LeadTimeContext';
import DealCard from './DealCard';
import PipelineManager from './PipelineManager';
import DealDetailsPanel from './DealDetailsPanel';
import SniperZone from './SniperZone';
import { Search, LayoutGrid, List, ChevronDown, ChevronRight, Trash2, Trophy, Eye, EyeOff, ArrowDown, Crosshair } from 'lucide-react';
import './DealPipeline.css';

interface DealPipelineProps {
  onDealSelect?: (deal: EnhancedDeal) => void;
}

type SortOption = 'value-desc' | 'value-asc' | 'days-desc' | 'days-asc' | 'created-desc' | 'created-asc';
type ViewMode = 'kanban' | 'condensed';

const UNASSIGNED_STAGE_ID = '__unassigned__';

/**
 * Map a deal's stageId to a column id on the *selected* pipeline.
 * Duplicate pipeline docs (same name, different Firestore ids) use different stage UUIDs; without this,
 * those deals land in "Other" even though the stage names match.
 */
function resolveDealToSelectedStageId(
  deal: EnhancedDeal,
  selectedPipeline: Pipeline,
  allPipelines: Pipeline[],
): string {
  if (!selectedPipeline?.stages?.length) return UNASSIGNED_STAGE_ID;

  if (selectedPipeline.stages.some(s => s.id === deal.stageId)) return deal.stageId;

  const name = selectedPipeline.name?.trim();
  if (!name) return UNASSIGNED_STAGE_ID;

  const sameName = allPipelines.filter(p => p.id && p.name?.trim() === name);

  let sourcePipeline = sameName.find(p => p.id === deal.pipelineId);
  let sourceStage = sourcePipeline?.stages?.find(s => s.id === deal.stageId);

  if (!sourceStage) {
    for (const p of sameName) {
      const st = p.stages?.find(s => s.id === deal.stageId);
      if (st) {
        sourcePipeline = p;
        sourceStage = st;
        break;
      }
    }
  }

  if (!sourceStage) return UNASSIGNED_STAGE_ID;

  const resolvedStage = sourceStage;
  const byName = selectedPipeline.stages.find(
    s => s.name.trim().toLowerCase() === resolvedStage.name.trim().toLowerCase(),
  );
  if (byName) return byName.id;

  if (sourcePipeline?.stages?.length) {
    const sourceSorted = [...sourcePipeline.stages].sort((a, b) => a.order - b.order);
    const selSorted = [...selectedPipeline.stages].sort((a, b) => a.order - b.order);
    const idx = sourceSorted.findIndex(s => s.id === deal.stageId);
    if (idx >= 0 && idx < selSorted.length) return selSorted[idx].id;
  }

  return UNASSIGNED_STAGE_ID;
}

/* ── Stage Column with scroll indicator ── */
interface StageColumnProps {
  stage: PipelineStage;
  stageTotals: { count: number; value: number };
  stageDeals: EnhancedDeal[];
  isOver: boolean;
  draggingDealId: string | null;
  leads: any[];
  isRotting: (deal: EnhancedDeal) => boolean;
  getDaysInStage: (deal: EnhancedDeal) => number;
  handleDealClick: (deal: EnhancedDeal) => void;
  handleDragStart: (e: React.DragEvent, dealId: string) => void;
  handleDragEnd: () => void;
  handleDragOver: (e: React.DragEvent, stageId: string) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, stageId: string) => void;
  formatCurrency: (value: number) => string;
}

const StageColumn: React.FC<StageColumnProps> = ({
  stage, stageTotals, stageDeals, isOver, draggingDealId, leads,
  isRotting, getDaysInStage, handleDealClick, handleDragStart,
  handleDragEnd, handleDragOver, handleDragLeave, handleDrop, formatCurrency,
}) => {
  const cardsRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const checkScroll = useCallback(() => {
    const el = cardsRef.current;
    if (!el) { setCanScrollDown(false); return; }
    const hasMore = el.scrollHeight - el.scrollTop - el.clientHeight > 8;
    setCanScrollDown(hasMore);
  }, []);

  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;

    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });

    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      ro.disconnect();
    };
  }, [checkScroll, stageDeals.length]);

  const scrollDown = () => {
    const el = cardsRef.current;
    if (el) el.scrollBy({ top: 200, behavior: 'smooth' });
  };

  return (
    <div
      className={`dp-column ${isOver ? 'dp-column-over' : ''}`}
      onDragOver={(e) => handleDragOver(e, stage.id)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, stage.id)}
    >
      <div className="dp-col-header">
        <div className="dp-col-title-row">
          <span className="dp-col-name">{stage.name}</span>
          <span className="dp-col-count">{stageTotals.count}</span>
        </div>
        <span className="dp-col-value">{formatCurrency(stageTotals.value)}</span>
      </div>

      <div className="dp-col-cards" ref={cardsRef}>
        {stageDeals.map(deal => {
          const linkedLead = deal.leadId ? leads.find((l: any) => l.id === deal.leadId) : undefined;
          return (
            <DealCard
              key={deal.id}
              deal={deal}
              isRotting={isRotting(deal)}
              daysInStage={getDaysInStage(deal)}
              isDragging={draggingDealId === deal.id}
              leadSource={linkedLead?.source}
              onClick={() => handleDealClick(deal)}
              onDragStart={(e) => deal.id && handleDragStart(e, deal.id)}
              onDragEnd={handleDragEnd}
            />
          );
        })}

        {stageDeals.length === 0 && !isOver && (
          <div className="dp-col-empty">No deals</div>
        )}

        {isOver && (
          <div className="dp-drop-zone">Drop here</div>
        )}
      </div>

      {canScrollDown && (
        <button className="dp-scroll-indicator" onClick={scrollDown} aria-label="Scroll down for more deals">
          <ArrowDown size={14} />
        </button>
      )}
    </div>
  );
};

export const DealPipeline: React.FC<DealPipelineProps> = ({ onDealSelect }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { setActiveLead } = useLeadTimeContext();
  const pendingDealIdFromNav = useRef<string | null>(null);
  const {
    pipelines,
    enhancedDeals,
    leads,
    moveDealToStage,
    initializeDefaultPipelines,
    deleteEnhancedDeal
  } = useCRM();

  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<EnhancedDeal | null>(null);

  // Time tracking: set active lead when viewing a deal in the pipeline
  useEffect(() => {
    if (selectedDeal?.leadId && selectedDeal?.id) {
      setActiveLead(selectedDeal.leadId, selectedDeal.id, undefined);
    } else {
      setActiveLead(null);
    }
    return () => setActiveLead(null);
  }, [selectedDeal?.leadId, selectedDeal?.id, setActiveLead]);
  const [showPipelineManager, setShowPipelineManager] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('value-desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [draggingDealId, setDraggingDealId] = useState<string | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [showLostDeals, setShowLostDeals] = useState(false);
  const [showWonDeals, setShowWonDeals] = useState(false);
  const [showSniperZone, setShowSniperZone] = useState(false);

  // Dynamic board height calculation
  const shellRef = useRef<HTMLDivElement>(null);
  const topbarRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [boardHeight, setBoardHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const calcHeight = () => {
      const shell = shellRef.current;
      const topbar = topbarRef.current;
      const controls = controlsRef.current;
      if (!shell || !topbar || !controls) return;
      const available = shell.clientHeight - topbar.offsetHeight - controls.offsetHeight;
      setBoardHeight(Math.max(available, 200));
    };

    calcHeight();
    window.addEventListener('resize', calcHeight);

    const ro = new ResizeObserver(calcHeight);
    if (shellRef.current) ro.observe(shellRef.current);

    return () => {
      window.removeEventListener('resize', calcHeight);
      ro.disconnect();
    };
  }, []);

  // Initialize / migrate pipelines on mount
  React.useEffect(() => {
    initializeDefaultPipelines();
  }, [initializeDefaultPipelines]);

  // Open a specific deal from navigation (e.g. deal_won notification → /pipeline)
  useEffect(() => {
    const st = location.state as { selectedDealId?: string } | null;
    if (!st?.selectedDealId) return;
    pendingDealIdFromNav.current = st.selectedDealId;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const id = pendingDealIdFromNav.current;
    if (!id) return;
    const deal = enhancedDeals.find(d => d.id === id);
    if (!deal) return;
    setSelectedDeal(deal);
    if (deal.pipelineId) setSelectedPipelineId(deal.pipelineId);
    if (deal.outcome === 'won') setShowWonDeals(true);
    if (deal.outcome === 'lost') setShowLostDeals(true);
    pendingDealIdFromNav.current = null;
  }, [enhancedDeals]);

  // Keep selectedDeal in sync with enhancedDeals (for real-time updates)
  React.useEffect(() => {
    if (selectedDeal?.id) {
      const updatedDeal = enhancedDeals.find(d => d.id === selectedDeal.id);
      if (updatedDeal && JSON.stringify(updatedDeal) !== JSON.stringify(selectedDeal)) {
        setSelectedDeal(updatedDeal);
      }
    }
  }, [enhancedDeals, selectedDeal]);

  // Deduplicate pipelines by name, preferring pipelines that have associated deals
  const uniquePipelines = useMemo(() => {
    // Count deals per pipeline to determine which duplicate to keep
    const dealCountByPipeline = new Map<string, number>();
    enhancedDeals.forEach(deal => {
      if (deal.pipelineId) {
        dealCountByPipeline.set(deal.pipelineId, (dealCountByPipeline.get(deal.pipelineId) || 0) + 1);
      }
    });

    const seen = new Map<string, Pipeline>();
    pipelines.forEach(pipeline => {
      const existing = seen.get(pipeline.name);
      if (!existing) {
        seen.set(pipeline.name, pipeline);
      } else {
        // Prefer the pipeline with more deals, then more stages
        const existingDealCount = dealCountByPipeline.get(existing.id || '') || 0;
        const newDealCount = dealCountByPipeline.get(pipeline.id || '') || 0;

        if (newDealCount > existingDealCount) {
          seen.set(pipeline.name, pipeline);
        } else if (newDealCount === existingDealCount && (pipeline.stages?.length || 0) > (existing.stages?.length || 0)) {
          seen.set(pipeline.name, pipeline);
        }
      }
    });
    return Array.from(seen.values());
  }, [pipelines, enhancedDeals]);

  // Set default pipeline when loaded, or reset if selected pipeline was removed (e.g. after migration)
  React.useEffect(() => {
    if (uniquePipelines.length > 0) {
      const currentExists = selectedPipelineId && uniquePipelines.some(p => p.id === selectedPipelineId);
      if (!currentExists) {
        const defaultPipeline = uniquePipelines[0];
        if (defaultPipeline?.id) {
          setSelectedPipelineId(defaultPipeline.id);
        }
      }
    }
  }, [uniquePipelines, selectedPipelineId]);

  const selectedPipeline = useMemo(() =>
    uniquePipelines.find(p => p.id === selectedPipelineId),
    [uniquePipelines, selectedPipelineId]
  );

  /** Resolves deal.stageId to a column on the currently selected pipeline (handles duplicate pipeline docs). */
  const resolveDisplayStageId = useCallback(
    (deal: EnhancedDeal) => {
      if (!selectedPipeline) return UNASSIGNED_STAGE_ID;
      return resolveDealToSelectedStageId(deal, selectedPipeline, pipelines);
    },
    [selectedPipeline, pipelines],
  );

  // Filter deals for selected pipeline. Include deals even when leadId is missing from leads
  // (e.g. imported deals whose lead may be in a different collection, or test/production mismatch).
  // Also include deals from pipelines with the same name (deduplication can keep one pipeline ID
  // while deals may reference another pipeline ID that shares the name, e.g. Oratory).
  const pipelineDeals = useMemo(() => {
    if (!selectedPipelineId || !selectedPipeline) return [];
    const outcomeFilter = showLostDeals ? 'lost' : showWonDeals ? 'won' : 'active';
    return enhancedDeals.filter(d => {
      const pipelineMatches =
        d.pipelineId === selectedPipelineId ||
        (!!selectedPipeline.name && pipelines.some(p => p.id === d.pipelineId && p.name === selectedPipeline.name));
      return pipelineMatches && d.outcome === outcomeFilter;
    });
  }, [enhancedDeals, selectedPipelineId, selectedPipeline, pipelines, showLostDeals, showWonDeals]);

  // Helper: does deal belong to selected pipeline view (direct or same-name)?
  const dealBelongsToPipeline = useCallback(
    (d: EnhancedDeal) =>
      d.pipelineId === selectedPipelineId ||
      (!!selectedPipeline?.name && pipelines.some(p => p.id === d.pipelineId && p.name === selectedPipeline.name)),
    [selectedPipelineId, selectedPipeline, pipelines]
  );

  // Count of lost deals for the badge
  const lostDealsCount = useMemo(() => {
    if (!selectedPipelineId) return 0;
    return enhancedDeals.filter(d => dealBelongsToPipeline(d) && d.outcome === 'lost').length;
  }, [enhancedDeals, selectedPipelineId, dealBelongsToPipeline]);

  // Count of won deals for the badge
  const wonDealsCount = useMemo(() => {
    if (!selectedPipelineId) return 0;
    return enhancedDeals.filter(d => dealBelongsToPipeline(d) && d.outcome === 'won').length;
  }, [enhancedDeals, selectedPipelineId, dealBelongsToPipeline]);

  // Note: We no longer auto-delete "orphaned" deals (deals whose leadId isn't in leads).
  // Imported deals may have leadIds that reference leads in a different collection (test vs prod),
  // and we now show them in the pipeline so they're accessible.

  // Apply search filter
  const filteredDeals = useMemo(() => {
    if (!searchQuery.trim()) return pipelineDeals;
    const query = searchQuery.toLowerCase();
    return pipelineDeals.filter(deal =>
      deal.title.toLowerCase().includes(query) ||
      deal.contactName.toLowerCase().includes(query) ||
      deal.location?.toLowerCase().includes(query)
    );
  }, [pipelineDeals, searchQuery]);

  // Sort deals within each stage
  const sortDeals = useCallback((deals: EnhancedDeal[]): EnhancedDeal[] => {
    return [...deals].sort((a, b) => {
      switch (sortBy) {
        case 'value-desc':
          return b.value - a.value;
        case 'value-asc':
          return a.value - b.value;
        case 'days-desc':
          return getDaysInStage(b) - getDaysInStage(a);
        case 'days-asc':
          return getDaysInStage(a) - getDaysInStage(b);
        case 'created-desc':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'created-asc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        default:
          return 0;
      }
    });
  }, [sortBy]);

  // Get deals for a specific stage (UNASSIGNED = could not map stageId onto selected pipeline columns)
  const getDealsForStage = useCallback((stageId: string): EnhancedDeal[] => {
    const stageDeals =
      stageId === UNASSIGNED_STAGE_ID
        ? filteredDeals.filter(d => resolveDisplayStageId(d) === UNASSIGNED_STAGE_ID)
        : filteredDeals.filter(d => resolveDisplayStageId(d) === stageId);
    return sortDeals(stageDeals);
  }, [filteredDeals, sortDeals, resolveDisplayStageId]);

  // Calculate stage totals
  const getStageTotals = useCallback((stageId: string) => {
    const stageDeals =
      stageId === UNASSIGNED_STAGE_ID
        ? pipelineDeals.filter(d => resolveDisplayStageId(d) === UNASSIGNED_STAGE_ID)
        : pipelineDeals.filter(d => resolveDisplayStageId(d) === stageId);
    const totalValue = stageDeals.reduce((sum, d) => sum + d.value, 0);
    return { count: stageDeals.length, value: totalValue };
  }, [pipelineDeals, resolveDisplayStageId]);

  // Calculate days in current stage
  const getDaysInStage = (deal: EnhancedDeal): number => {
    // Handle missing or invalid currentStageEnteredAt
    let enteredAt: Date;

    if (deal.currentStageEnteredAt) {
      enteredAt = new Date(deal.currentStageEnteredAt);
    } else if (deal.createdAt) {
      // Fallback to createdAt if currentStageEnteredAt is missing
      enteredAt = new Date(deal.createdAt);
    } else {
      return 0; // No valid date available
    }

    // Check if date is valid and reasonable (not before year 2000)
    const minValidDate = new Date('2000-01-01').getTime();
    if (isNaN(enteredAt.getTime()) || enteredAt.getTime() < minValidDate) {
      // If currentStageEnteredAt is invalid, try createdAt
      if (deal.createdAt) {
        const createdDate = new Date(deal.createdAt);
        if (!isNaN(createdDate.getTime()) && createdDate.getTime() >= minValidDate) {
          enteredAt = createdDate;
        } else {
          return 0; // No valid date
        }
      } else {
        return 0;
      }
    }

    const now = new Date();
    const difference = now.getTime() - enteredAt.getTime();
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));

    // Sanity check - if days is negative or unreasonably large, return 0
    if (days < 0 || days > 3650) { // More than 10 years is likely invalid
      return 0;
    }

    return days;
  };

  // Check if deal is rotting
  const isRotting = useCallback((deal: EnhancedDeal): boolean => {
    if (!selectedPipeline) return false;
    const displayId = resolveDisplayStageId(deal);
    const stage = selectedPipeline.stages.find(s => s.id === displayId);
    if (!stage?.rottingDays) return false;
    return getDaysInStage(deal) > stage.rottingDays;
  }, [selectedPipeline, resolveDisplayStageId]);

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, dealId: string) => {
    setDraggingDealId(dealId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', dealId);
  };

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStageId !== stageId) {
      setDragOverStageId(stageId);
    }
  };

  const handleDragLeave = () => {
    setDragOverStageId(null);
  };

  const handleDrop = async (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    setDragOverStageId(null);

    const dealId = e.dataTransfer.getData('text/plain');
    if (!dealId || !user?.id) return;

    const deal = enhancedDeals.find(d => d.id === dealId);
    if (!deal) {
      setDraggingDealId(null);
      return;
    }

    // Dropping on "Other" moves deal to first pipeline stage (assigns it properly)
    const targetStageId =
      stageId === UNASSIGNED_STAGE_ID && selectedPipeline?.stages?.length
        ? selectedPipeline.stages.sort((a, b) => a.order - b.order)[0].id
        : stageId;

    if (resolveDisplayStageId(deal) === targetStageId) {
      setDraggingDealId(null);
      return;
    }

    try {
      await moveDealToStage(dealId, targetStageId, user.id);
    } catch (err) {
      console.error('Error moving deal:', err);
    }

    setDraggingDealId(null);
  };

  const handleDragEnd = () => {
    setDraggingDealId(null);
    setDragOverStageId(null);
  };

  const handleDealClick = (deal: EnhancedDeal) => {
    setSelectedDeal(deal);
    onDealSelect?.(deal);
  };

  // Toggle stage expansion in condensed view
  const toggleStageExpansion = (stageId: string) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(stageId)) {
        next.delete(stageId);
      } else {
        next.add(stageId);
      }
      return next;
    });
  };

  // Expand all stages
  const expandAllStages = () => {
    if (selectedPipeline) {
      setExpandedStages(new Set(selectedPipeline.stages.map(s => s.id)));
    }
  };

  // Stages to render: real stages + "Other" if there are deals with non-matching stageIds
  const stagesToRender = useMemo(() => {
    if (!selectedPipeline?.stages) return [];
    const realStages = [...selectedPipeline.stages].sort((a, b) => a.order - b.order);
    const unassignedCount = pipelineDeals.filter(d => resolveDisplayStageId(d) === UNASSIGNED_STAGE_ID).length;
    if (unassignedCount === 0) return realStages;
    return [
      ...realStages,
      { id: UNASSIGNED_STAGE_ID, name: 'Other', order: 999 } as PipelineStage,
    ];
  }, [selectedPipeline, pipelineDeals, resolveDisplayStageId]);

  // Collapse all stages
  const collapseAllStages = () => {
    setExpandedStages(new Set());
  };

  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  // Pipeline statistics
  const pipelineStats = useMemo(() => {
    const activeDeals = pipelineDeals.length;
    const totalValue = pipelineDeals.reduce((sum, d) => sum + d.value, 0);
    const rottingCount = pipelineDeals.filter(d => isRotting(d)).length;
    return { activeDeals, totalValue, rottingCount };
  }, [pipelineDeals, isRotting]);

  const sniperCount = useMemo(() =>
    enhancedDeals.filter(d => d.sniperZone && d.outcome === 'active').length,
    [enhancedDeals],
  );

  const hasCriticalSniper = useMemo(() => {
    if (!selectedPipeline || sniperCount === 0) return false;
    return enhancedDeals.some(d => {
      if (!d.sniperZone || d.outcome !== 'active') return false;
      const displayId = resolveDisplayStageId(d);
      const stage = selectedPipeline.stages.find(s => s.id === displayId);
      const rottingDays = stage?.rottingDays ?? 30;
      const entered = new Date(d.currentStageEnteredAt || d.createdAt);
      const days = Math.floor((Date.now() - entered.getTime()) / 86400000);
      return days > rottingDays * 2;
    });
  }, [enhancedDeals, selectedPipeline, sniperCount, resolveDisplayStageId]);

  if (!selectedPipeline) {
    return (
      <div className="dp-shell dp-empty">
        <h2>No Pipeline Selected</h2>
        <p>Create a pipeline to start managing deals.</p>
        <button onClick={() => setShowPipelineManager(true)}>
          Create Pipeline
        </button>
        {showPipelineManager && (
          <PipelineManager onClose={() => setShowPipelineManager(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="dp-shell" ref={shellRef}>
      {/* ── Top Bar: Title + Stats + Pipeline Tabs ── */}
      <div className="dp-topbar" ref={topbarRef}>
        <div className="dp-topbar-left">
          <h1 className="dp-title">Deals</h1>
          <div className="dp-stats">
            <span className="dp-stat">
              <strong>{pipelineStats.activeDeals}</strong> deals
            </span>
            <span className="dp-stat-sep">·</span>
            <span className="dp-stat">
              <strong>{formatCurrency(pipelineStats.totalValue)}</strong> total
            </span>
            {pipelineStats.rottingCount > 0 && (
              <>
                <span className="dp-stat-sep">·</span>
                <span className="dp-stat dp-stat-warn">
                  <strong>{pipelineStats.rottingCount}</strong> stalled
                </span>
              </>
            )}
          </div>
        </div>
        <div className="dp-topbar-right">
          <button
            className={`dp-sniper-btn ${hasCriticalSniper ? 'dp-sniper-btn-pulse' : ''}`}
            onClick={() => setShowSniperZone(true)}
            title="Sniper Zone — deals requiring immediate focus"
          >
            <Crosshair size={13} />
            <span className="dp-sniper-label">Sniper Zone</span>
            {sniperCount > 0 && <span className="dp-sniper-count">{sniperCount}</span>}
          </button>
        </div>
      </div>

      {/* ── Controls Row: Search + Actions ── */}
      <div className="dp-controls" ref={controlsRef}>
        <div className="dp-controls-left">
          <div className="dp-search">
            <Search size={15} className="dp-search-icon" />
            <input
              type="text"
              placeholder="Search deals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="dp-controls-right">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="dp-sort"
          >
            <option value="value-desc">Value: High to Low</option>
            <option value="value-asc">Value: Low to High</option>
            <option value="days-desc">Days in Stage: Most</option>
            <option value="days-asc">Days in Stage: Least</option>
            <option value="created-desc">Created: Newest</option>
            <option value="created-asc">Created: Oldest</option>
          </select>

          <div className="dp-view-toggle">
            <button
              className={`dp-view-btn ${viewMode === 'kanban' ? 'dp-view-active' : ''}`}
              onClick={() => setViewMode('kanban')}
              title="Board"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              className={`dp-view-btn ${viewMode === 'condensed' ? 'dp-view-active' : ''}`}
              onClick={() => setViewMode('condensed')}
              title="List"
            >
              <List size={15} />
            </button>
          </div>

          <button
            className={`dp-filter-btn ${showLostDeals ? 'dp-filter-active dp-filter-lost' : ''}`}
            onClick={() => {
              setShowLostDeals(!showLostDeals);
              if (!showLostDeals) setShowWonDeals(false);
            }}
          >
            <Trash2 size={14} />
            <span>Lost</span>
            {!showLostDeals && lostDealsCount > 0 && (
              <span className="dp-badge dp-badge-lost">{lostDealsCount}</span>
            )}
          </button>

          <button
            className={`dp-filter-btn ${showWonDeals ? 'dp-filter-active dp-filter-won' : ''}`}
            onClick={() => {
              setShowWonDeals(!showWonDeals);
              if (!showWonDeals) setShowLostDeals(false);
            }}
          >
            <Trophy size={14} />
            <span>Won</span>
            {!showWonDeals && wonDealsCount > 0 && (
              <span className="dp-badge dp-badge-won">{wonDealsCount}</span>
            )}
          </button>

          {viewMode === 'condensed' && (
            <div className="dp-expand-btns">
              <button onClick={expandAllStages} className="dp-small-btn">Expand All</button>
              <button onClick={collapseAllStages} className="dp-small-btn">Collapse All</button>
            </div>
          )}
        </div>
      </div>

      {viewMode === 'kanban' && (
        <div className="dp-board" style={boardHeight ? { height: boardHeight } : undefined}>
          {stagesToRender.map(stage => {
              const stageTotals = getStageTotals(stage.id);
              const stageDeals = getDealsForStage(stage.id);
              const isOver = dragOverStageId === stage.id;

              return (
                <StageColumn
                  key={stage.id}
                  stage={stage}
                  stageTotals={stageTotals}
                  stageDeals={stageDeals}
                  isOver={isOver}
                  draggingDealId={draggingDealId}
                  leads={leads}
                  isRotting={isRotting}
                  getDaysInStage={getDaysInStage}
                  handleDealClick={handleDealClick}
                  handleDragStart={handleDragStart}
                  handleDragEnd={handleDragEnd}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  formatCurrency={formatCurrency}
                />
              );
            })}
        </div>
      )}

      {/* ── Condensed View ── */}
      {viewMode === 'condensed' && (
        <div className="dp-condensed">
          {stagesToRender.map(stage => {
              const stageTotals = getStageTotals(stage.id);
              const stageDeals = getDealsForStage(stage.id);
              const isExpanded = expandedStages.has(stage.id);
              const isOver = dragOverStageId === stage.id;

              return (
                <div
                  key={stage.id}
                  className={`dp-cond-stage ${isExpanded ? 'dp-cond-open' : ''} ${isOver ? 'dp-cond-over' : ''}`}
                  onDragOver={(e) => handleDragOver(e, stage.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  <div
                    className="dp-cond-header"
                    onClick={() => toggleStageExpansion(stage.id)}
                  >
                    <div className="dp-cond-left">
                      <span className="dp-cond-chevron">
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </span>
                      <span className="dp-cond-name">{stage.name}</span>
                      <span className="dp-cond-count">{stageTotals.count}</span>
                    </div>
                    <span className="dp-cond-value">{formatCurrency(stageTotals.value)}</span>
                  </div>

                  {isExpanded && (
                    <div className="dp-cond-body">
                      {stageDeals.length === 0 ? (
                        <div className="dp-cond-empty">No deals in this stage</div>
                      ) : (
                        <div className="dp-cond-grid">
                          {stageDeals.map(deal => {
                            const linkedLead = deal.leadId ? leads.find(l => l.id === deal.leadId) : undefined;
                            return (
                              <DealCard
                                key={deal.id}
                                deal={deal}
                                isRotting={isRotting(deal)}
                                daysInStage={getDaysInStage(deal)}
                                isDragging={draggingDealId === deal.id}
                                leadSource={linkedLead?.source}
                                onClick={() => handleDealClick(deal)}
                                onDragStart={(e) => deal.id && handleDragStart(e, deal.id)}
                                onDragEnd={handleDragEnd}
                                compact
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {isOver && !isExpanded && (
                    <div className="dp-cond-drop">Drop to move deal here</div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Deal Details Panel */}
      {selectedDeal && (
        <DealDetailsPanel
          deal={selectedDeal}
          pipeline={selectedPipeline}
          onClose={() => setSelectedDeal(null)}
          userId={user?.id || 'unknown'}
        />
      )}

      {/* Sniper Zone Modal */}
      {showSniperZone && selectedPipeline && (
        <SniperZone
          pipeline={selectedPipeline}
          allDeals={enhancedDeals}
          onClose={() => setShowSniperZone(false)}
          onDealClick={(deal) => {
            setShowSniperZone(false);
            setSelectedDeal(deal);
          }}
        />
      )}

      {/* Pipeline Manager Modal */}
      {showPipelineManager && (
        <PipelineManager onClose={() => setShowPipelineManager(false)} />
      )}
    </div>
  );
};

export default DealPipeline;
